// @flow
import { handleActions } from "redux-actions";
import queryString from "query-string";

import { CALL_API } from "redux/middleware/api";
import type { callApiReturnType } from "redux/middleware/api";
import { APP_NAMESPACE } from "shared/constants/application";
import {
  NONE,
  LOADING,
  LOADED,
  ERROR,
  PENDING,
  SUCCESS,
} from "shared/constants/status";
import type {
  simpleReduxAction,
  apiErrorReturn,
} from "shared/constants/flowTypes";
import {
  BIZHUB_API_ENDPOINTS,
  CABELAS_BUSINESS_SLUG,
  EVENTS_API_ENDPOINTS,
} from "redux/modules/createCabelasClass/constants";
import type { EventRecordI } from "./EventRecord";
import type { EventRegistrantRecordI } from "./RegistrantRecord";
import type { ReviewActionRecordI } from "./ReviewActionRecord";
import type {
  BizhubEvent,
  EventRegistrant,
  EventReviewAction,
} from "./parsers";
import {
  parseEventReturnToRecord,
  parseEvents,
  parseRegistrants,
  parseReviewActions,
} from "./parsers";

const namespace: string = `${APP_NAMESPACE}/instructorCabelasClasses`;

export const LIVE_STATUS = "live";
export const CLOSED_STATUS = "closed";
export const DRAFT_STATUS = "draft";
export const CANCELLED_STATUS = "cancelled";
export const CONFIRMED_REGISTRANT_STATUS = "confirmed";
export const ALL_SUBMITTED_STATUSES = "all-submitted"; // _neq=cancelled; Events API has no _in
export const ALL_REVIEW_STATUSES = "";
export const PENDING_REVIEW_STATUS = "pending";
export const APPROVED_REVIEW_STATUS = "approved";
export const DENIED_REVIEW_STATUS = "denied";
export const FLAGGED_REVIEW_STATUS = "flagged";
export const UPCOMING_VIEW = "upcoming";
export const COMPLETED_VIEW = "completed";
export const SUBMITTED_VIEW = "submitted";
export const ASC = "asc";
export const DESC = "desc";
export const DEFAULT_PER_PAGE = 10;
export const MAX_PER_PAGE = 50;
export const DEFAULT_SORT_COLUMN = "startDate";
export const DEFAULT_SORT_DIRECTION = ASC;

export const CABELAS_CLASSES_REQUEST: string = `${namespace}/FETCH/REQUEST`;
export const CABELAS_CLASSES_REQUEST_SUCCESS: string = `${namespace}/FETCH/SUCCESS`;
export const CABELAS_CLASSES_REQUEST_ERROR: string = `${namespace}/FETCH/ERROR`;
export const ROSTER_EVENT_REQUEST: string = `${namespace}/ROSTER_EVENT/FETCH/REQUEST`;
export const ROSTER_EVENT_REQUEST_SUCCESS: string = `${namespace}/ROSTER_EVENT/FETCH/SUCCESS`;
export const ROSTER_EVENT_REQUEST_ERROR: string = `${namespace}/ROSTER_EVENT/FETCH/ERROR`;
export const EVENT_REGISTRANTS_REQUEST: string = `${namespace}/REGISTRANTS/FETCH/REQUEST`;
export const EVENT_REGISTRANTS_REQUEST_SUCCESS: string = `${namespace}/REGISTRANTS/FETCH/SUCCESS`;
export const EVENT_REGISTRANTS_REQUEST_ERROR: string = `${namespace}/REGISTRANTS/FETCH/ERROR`;
export const EVENT_HOST_REQUEST: string = `${namespace}/HOST/PATCH/REQUEST`;
export const EVENT_HOST_REQUEST_SUCCESS: string = `${namespace}/HOST/PATCH/SUCCESS`;
export const EVENT_HOST_REQUEST_ERROR: string = `${namespace}/HOST/PATCH/ERROR`;
export const EVENT_UPDATE_REQUEST: string = `${namespace}/EVENT/PATCH/REQUEST`;
export const EVENT_UPDATE_REQUEST_SUCCESS: string = `${namespace}/EVENT/PATCH/SUCCESS`;
export const EVENT_UPDATE_REQUEST_ERROR: string = `${namespace}/EVENT/PATCH/ERROR`;
export const EVENT_REVIEW_ACTIONS_REQUEST: string = `${namespace}/REVIEW_ACTIONS/FETCH/REQUEST`;
export const EVENT_REVIEW_ACTIONS_REQUEST_SUCCESS: string = `${namespace}/REVIEW_ACTIONS/FETCH/SUCCESS`;
export const EVENT_REVIEW_ACTIONS_REQUEST_ERROR: string = `${namespace}/REVIEW_ACTIONS/FETCH/ERROR`;
export const REVIEW_STATUS_COUNTS_REQUEST: string = `${namespace}/REVIEW_STATUS_COUNTS/FETCH/REQUEST`;
export const REVIEW_STATUS_COUNTS_REQUEST_SUCCESS: string = `${namespace}/REVIEW_STATUS_COUNTS/FETCH/SUCCESS`;
export const REVIEW_STATUS_COUNTS_REQUEST_ERROR: string = `${namespace}/REVIEW_STATUS_COUNTS/FETCH/ERROR`;
export const EVENT_REVIEW_ACTION_CREATE_REQUEST: string = `${namespace}/REVIEW_ACTION/CREATE/REQUEST`;
export const EVENT_REVIEW_ACTION_CREATE_SUCCESS: string = `${namespace}/REVIEW_ACTION/CREATE/SUCCESS`;
export const EVENT_REVIEW_ACTION_CREATE_ERROR: string = `${namespace}/REVIEW_ACTION/CREATE/ERROR`;
export const CHECK_IN_REQUEST: string = `${namespace}/CHECK_IN/POST/REQUEST`;
export const CHECK_IN_REQUEST_SUCCESS: string = `${namespace}/CHECK_IN/POST/SUCCESS`;
export const CHECK_IN_REQUEST_ERROR: string = `${namespace}/CHECK_IN/POST/ERROR`;
export const BUSINESS_REQUEST: string = `${namespace}/BUSINESS/FETCH/REQUEST`;
export const BUSINESS_REQUEST_SUCCESS: string = `${namespace}/BUSINESS/FETCH/SUCCESS`;
export const BUSINESS_REQUEST_ERROR: string = `${namespace}/BUSINESS/FETCH/ERROR`;
export const CLEAR_ROSTER: string = `${namespace}/ROSTER/CLEAR`;
export const CLEAR: string = `${namespace}/CLEAR`;

let requestSequence = 0;
const nextRequestId = (): number => {
  requestSequence += 1;
  return requestSequence;
};

type BusinessReturn = {
  data: {
    id: string,
  },
};

type ReceiveBusinessReturn = {
  type: string,
  payload: BusinessReturn,
  meta: {
    receivedAt: number,
  },
};

export type CabelasClassesParams = {
  eventStatus?: string,
  reviewStatus?: string,
  eventView?: string,
  businessId?: string,
  page?: number,
  currentPage?: number, // also accepted so normalizeParams is idempotent
  perPage?: number,
  search?: string,
  sortColumn?: string,
  sortDirection?: string,
  requestId?: number,
};

export type NormalizedCabelasClassesParams = {
  eventStatus: string,
  reviewStatus: string,
  eventView: string,
  currentPage: number,
  perPage: number,
  search: string,
  sortColumn: string,
  sortDirection: string,
};

type RequestCabelasClassesReturn = {
  type: string,
  payload: NormalizedCabelasClassesParams & {
    requestId: number,
  },
};

type PaginationMeta = {
  [string]: mixed,
  total?: number,
  count?: number,
  perPage?: number,
  currentPage?: number,
  totalPages?: number,
};

type CabelasClassesReturn = {
  data: Array<BizhubEvent>,
  meta?: PaginationMeta,
};

type ReceiveCabelasClassesReturn = {
  type: string,
  payload: CabelasClassesReturn,
  meta: {
    receivedAt: number,
    requestId: number,
  },
};

type RosterEventParams = {
  eventId: string,
  requestId?: number,
};

type RequestRosterEventReturn = {
  type: string,
  payload: {
    eventId: string,
    requestId: number,
  },
};

type RosterEventReturn = {
  data: BizhubEvent,
};

type ReceiveRosterEventReturn = {
  type: string,
  payload: RosterEventReturn,
  meta: {
    receivedAt: number,
    requestId: number,
  },
};

type EventRegistrantsParams = {
  eventId: string,
  page?: number,
  currentPage?: number, // also accepted so normalizeRegistrantsParams is idempotent
  perPage?: number,
  search?: string,
  sortColumn?: string,
  sortDirection?: string,
  requestId?: number,
};

type NormalizedEventRegistrantsParams = {
  eventId: string,
  currentPage: number,
  perPage: number,
  search: string,
  sortColumn: string,
  sortDirection: string,
  requestId: number,
};

type RequestEventRegistrantsReturn = {
  type: string,
  payload: NormalizedEventRegistrantsParams,
};

type EventRegistrantsReturn = {
  data: Array<EventRegistrant>,
  meta?: PaginationMeta,
};

type ReceiveEventRegistrantsReturn = {
  type: string,
  payload: EventRegistrantsReturn,
  meta: {
    receivedAt: number,
    requestId: number,
  },
};

type EventHostParams = {
  eventId: string,
  hostUserId: string,
};

type EventHostAction = {
  type: string,
  payload: EventHostParams,
};

export type EventUpdateValues = {
  status?: string,
  startDate?: string,
  endDate?: string,
  closeDate?: string,
  actionNote?: string,
};

type EventUpdateParams = {
  eventId: string,
  updates: EventUpdateValues,
};

type EventUpdateAction = {
  type: string,
  payload: EventUpdateParams & {
    event?: BizhubEvent,
  },
};

type EventReviewActionsReturn = {
  data: Array<EventReviewAction>,
};

type EventReviewActionsAction = {
  type: string,
  payload: {
    eventId: string,
    reviewActions?: Array<ReviewActionRecordI>,
  },
};

export type ReviewStatusCounts = {
  total: number,
  pending: number,
  approved: number,
  flagged: number,
  denied: number,
};

type ReviewStatusCountsParams = {
  businessId?: string,
  requestId?: number,
};

type RequestReviewStatusCountsReturn = {
  type: string,
  payload: {
    requestId: number,
  },
};

type ReceiveReviewStatusCountsReturn = {
  type: string,
  payload: ReviewStatusCounts,
  meta: {
    receivedAt: number,
    requestId: number,
  },
};

type CreateEventReviewActionParams = {
  eventId: string,
  actionNote: string,
};

type CreateEventReviewActionAction = {
  type: string,
  payload: CreateEventReviewActionParams & {
    reviewAction?: ReviewActionRecordI,
  },
};

type CheckInRegistrantParams = {
  registrantId: string,
};

type CheckInRegistrantAction = {
  type: string,
  payload: CheckInRegistrantParams,
};

type CabelasApiActionParams = {
  requestId?: number,
  eventId?: string,
  registrantId?: string,
  businessId?: string,
  actionNote?: string,
};

type CabelasApiErrorParams = {
  errorMessage?: string,
  errors?: apiErrorReturn,
  actionParams?: CabelasApiActionParams,
};

type CabelasApiErrorAction = {
  type: string,
  error: true,
  payload: {
    errorMessage: string,
    errors: apiErrorReturn,
    actionParams?: CabelasApiActionParams,
  },
  meta: {
    receivedAt: number,
  },
};

const errorPayload = (
  error: CabelasApiErrorParams
): $PropertyType<CabelasApiErrorAction, "payload"> => ({
  errorMessage: error.errorMessage || "",
  errors: error.errors || [],
  actionParams: error.actionParams,
});

const metaValue = (
  meta: ?PaginationMeta,
  key: string,
  fallback: number
): number => {
  if (meta && meta.hasOwnProperty(key)) {
    const value = meta[key];
    if (typeof value === "number") {
      return value;
    }
  }
  return fallback;
};

const errorRequestId = (action: CabelasApiErrorAction): number => {
  const actionParams = action.payload.actionParams;
  const requestId = actionParams ? actionParams.requestId : 0;
  return typeof requestId === "number" ? requestId : 0;
};

// Idempotent: middleware re-normalizes actionParams; accept page or currentPage.
const normalizeParams = (
  params: CabelasClassesParams = {}
): NormalizedCabelasClassesParams => ({
  eventStatus:
    params.eventStatus === undefined ? LIVE_STATUS : params.eventStatus,
  reviewStatus: params.reviewStatus || ALL_REVIEW_STATUSES,
  eventView: params.eventView || UPCOMING_VIEW,
  currentPage: params.page || params.currentPage || 1,
  perPage: Math.min(params.perPage || DEFAULT_PER_PAGE, MAX_PER_PAGE),
  search: params.search || "",
  sortColumn: params.sortColumn || DEFAULT_SORT_COLUMN,
  sortDirection: params.sortDirection || DEFAULT_SORT_DIRECTION,
});

const normalizeRegistrantsParams = (
  params: EventRegistrantsParams
): NormalizedEventRegistrantsParams => ({
  eventId: params.eventId,
  currentPage: params.page || params.currentPage || 1,
  perPage: Math.min(params.perPage || DEFAULT_PER_PAGE, MAX_PER_PAGE),
  search: params.search || "",
  sortColumn: params.sortColumn || "createdAt",
  sortDirection: params.sortDirection || DEFAULT_SORT_DIRECTION,
  requestId: params.requestId || nextRequestId(),
});

export const requestCabelasClasses = (
  params: CabelasClassesParams = {}
): RequestCabelasClassesReturn => {
  const normalizedParams = normalizeParams(params);

  return {
    type: CABELAS_CLASSES_REQUEST,
    payload: {
      ...normalizedParams,
      requestId: params.requestId || nextRequestId(),
    },
  };
};

export const receiveCabelasClasses = (
  json: CabelasClassesReturn,
  params: CabelasClassesParams = {}
): ReceiveCabelasClassesReturn => ({
  type: CABELAS_CLASSES_REQUEST_SUCCESS,
  payload: json,
  meta: {
    receivedAt: Date.now(),
    requestId: params.requestId || 0,
  },
});

export const cabelasClassesRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: CABELAS_CLASSES_REQUEST_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const clear = (): simpleReduxAction => ({
  type: CLEAR,
});

export const clearRoster = (): simpleReduxAction => ({
  type: CLEAR_ROSTER,
});

export const requestRosterEvent = (
  params: RosterEventParams
): RequestRosterEventReturn => ({
  type: ROSTER_EVENT_REQUEST,
  payload: {
    eventId: params.eventId,
    requestId: params.requestId || nextRequestId(),
  },
});

export const receiveRosterEvent = (
  json: RosterEventReturn,
  params: RosterEventParams = { eventId: "" }
): ReceiveRosterEventReturn => ({
  type: ROSTER_EVENT_REQUEST_SUCCESS,
  payload: json,
  meta: {
    receivedAt: Date.now(),
    requestId: params.requestId || 0,
  },
});

export const rosterEventRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: ROSTER_EVENT_REQUEST_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestEventRegistrants = (
  params: EventRegistrantsParams
): RequestEventRegistrantsReturn => ({
  type: EVENT_REGISTRANTS_REQUEST,
  payload: normalizeRegistrantsParams(params),
});

export const receiveEventRegistrants = (
  json: EventRegistrantsReturn,
  params: EventRegistrantsParams = { eventId: "" }
): ReceiveEventRegistrantsReturn => ({
  type: EVENT_REGISTRANTS_REQUEST_SUCCESS,
  payload: json,
  meta: {
    receivedAt: Date.now(),
    requestId: params.requestId || 0,
  },
});

export const eventRegistrantsRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: EVENT_REGISTRANTS_REQUEST_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestSetEventHost = (
  params: EventHostParams
): EventHostAction => ({
  type: EVENT_HOST_REQUEST,
  payload: params,
});

export const receiveSetEventHost = (
  json: mixed,
  params: EventHostParams
): EventHostAction => ({
  type: EVENT_HOST_REQUEST_SUCCESS,
  payload: params,
});

export const setEventHostRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: EVENT_HOST_REQUEST_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestUpdateEvent = (
  params: EventUpdateParams
): EventUpdateAction => ({
  type: EVENT_UPDATE_REQUEST,
  payload: params,
});

export const receiveUpdateEvent = (
  json: { data?: BizhubEvent },
  params: EventUpdateParams
): EventUpdateAction => ({
  type: EVENT_UPDATE_REQUEST_SUCCESS,
  payload: {
    ...params,
    event: json.data,
  },
});

export const updateEventRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: EVENT_UPDATE_REQUEST_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestEventReviewActions = ({
  eventId,
}: {
  eventId: string,
}): EventReviewActionsAction => ({
  type: EVENT_REVIEW_ACTIONS_REQUEST,
  payload: { eventId },
});

export const receiveEventReviewActions = (
  json: EventReviewActionsReturn,
  params: { eventId: string }
): EventReviewActionsAction => ({
  type: EVENT_REVIEW_ACTIONS_REQUEST_SUCCESS,
  payload: {
    eventId: params.eventId,
    reviewActions: parseReviewActions(json.data),
  },
});

export const eventReviewActionsRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: EVENT_REVIEW_ACTIONS_REQUEST_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestReviewStatusCounts = (
  params: ReviewStatusCountsParams = {}
): RequestReviewStatusCountsReturn => ({
  type: REVIEW_STATUS_COUNTS_REQUEST,
  payload: {
    requestId: params.requestId || nextRequestId(),
  },
});

export const receiveReviewStatusCounts = (
  json: ReviewStatusCounts,
  params: ReviewStatusCountsParams = {}
): ReceiveReviewStatusCountsReturn => ({
  type: REVIEW_STATUS_COUNTS_REQUEST_SUCCESS,
  payload: json,
  meta: {
    receivedAt: Date.now(),
    requestId: params.requestId || 0,
  },
});

export const reviewStatusCountsRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: REVIEW_STATUS_COUNTS_REQUEST_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestCreateEventReviewAction = (
  params: CreateEventReviewActionParams
): CreateEventReviewActionAction => ({
  type: EVENT_REVIEW_ACTION_CREATE_REQUEST,
  payload: params,
});

export const receiveCreateEventReviewAction = (
  json: { data: EventReviewAction },
  params: CreateEventReviewActionParams
): CreateEventReviewActionAction => ({
  type: EVENT_REVIEW_ACTION_CREATE_SUCCESS,
  payload: {
    ...params,
    reviewAction: parseReviewActions([json.data])[0],
  },
});

export const createEventReviewActionRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: EVENT_REVIEW_ACTION_CREATE_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestCheckInRegistrant = (
  params: CheckInRegistrantParams
): CheckInRegistrantAction => ({
  type: CHECK_IN_REQUEST,
  payload: params,
});

export const receiveCheckInRegistrant = (
  json: mixed,
  params: CheckInRegistrantParams
): CheckInRegistrantAction => ({
  type: CHECK_IN_REQUEST_SUCCESS,
  payload: params,
});

export const checkInRegistrantRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: CHECK_IN_REQUEST_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestCabelasBusiness = (): simpleReduxAction => ({
  type: BUSINESS_REQUEST,
});

export const receiveCabelasBusiness = (
  json: BusinessReturn
): ReceiveBusinessReturn => ({
  type: BUSINESS_REQUEST_SUCCESS,
  payload: json,
  meta: {
    receivedAt: Date.now(),
  },
});

export const cabelasBusinessRequestError = (
  error: CabelasApiErrorParams
): CabelasApiErrorAction => ({
  type: BUSINESS_REQUEST_ERROR,
  error: true,
  payload: errorPayload(error),
  meta: {
    receivedAt: Date.now(),
  },
});

export const getCabelasBusiness = (): callApiReturnType => ({
  type: CALL_API,
  payload: {
    method: "GET",
    endpoint: BIZHUB_API_ENDPOINTS.BUSINESS(CABELAS_BUSINESS_SLUG),
    actions: {
      request: requestCabelasBusiness,
      success: receiveCabelasBusiness,
      failure: cabelasBusinessRequestError,
    },
  },
});

export const getCabelasClasses = (
  params: CabelasClassesParams = {}
): callApiReturnType => {
  const normalizedParams = requestCabelasClasses(params).payload;
  const {
    eventStatus,
    reviewStatus,
    eventView,
    currentPage,
    perPage,
    search,
    sortColumn,
    sortDirection,
  } = normalizedParams;
  const { businessId = "" } = params;

  const sortPrefix = sortDirection === DESC ? "-" : "";
  const queryParams: { [string]: string | number } = {
    page: currentPage,
    perPage,
    sort: `${sortPrefix}${sortColumn}`,
  };
  const isSubmittedView = eventView === SUBMITTED_VIEW;

  if (eventStatus === ALL_SUBMITTED_STATUSES) {
    queryParams["filter[status][_neq]"] = CANCELLED_STATUS;
  } else if (eventStatus) {
    queryParams["filter[status][_eq]"] = eventStatus;
  }

  if (isSubmittedView) {
    queryParams["filter[businessId][_eq]"] = businessId;

    if (reviewStatus) {
      queryParams["filter[reviewStatus][_eq]"] = reviewStatus;
    }
  }

  if (search.trim()) {
    queryParams["filter[name][_contains]"] = search.trim();
  }

  return {
    type: CALL_API,
    payload: {
      method: "GET",
      endpoint: `${
        isSubmittedView
          ? EVENTS_API_ENDPOINTS.ALL_EVENTS
          : BIZHUB_API_ENDPOINTS.BUSINESS_EVENTS(CABELAS_BUSINESS_SLUG)
      }?${queryString.stringify(queryParams)}`,
      actionParams: normalizedParams,
      actions: {
        request: requestCabelasClasses,
        success: receiveCabelasClasses,
        failure: cabelasClassesRequestError,
      },
    },
  };
};

export const getRosterEvent = (eventId: string): callApiReturnType => {
  const actionParams = requestRosterEvent({ eventId }).payload;

  return {
    type: CALL_API,
    payload: {
      method: "GET",
      endpoint: `${EVENTS_API_ENDPOINTS.EVENTS}/${eventId}`,
      actionParams,
      actions: {
        request: requestRosterEvent,
        success: receiveRosterEvent,
        failure: rosterEventRequestError,
      },
    },
  };
};

export const getEventRegistrants = (
  params: EventRegistrantsParams
): callApiReturnType => {
  const normalizedParams = normalizeRegistrantsParams(params);
  const { eventId, currentPage, perPage, search, sortColumn, sortDirection } =
    normalizedParams;
  const sortPrefix = sortDirection === DESC ? "-" : "";
  const queryParams: { [string]: string | number } = {
    page: currentPage,
    perPage,
    sort: `${sortPrefix}${sortColumn}`,
  };

  if (search.trim()) {
    queryParams["filter[lastName][_contains]"] = search.trim();
  }

  return {
    type: CALL_API,
    payload: {
      method: "GET",
      endpoint: `/api/events/events/${eventId}/registrants?${queryString.stringify(
        queryParams
      )}`,
      actionParams: normalizedParams,
      actions: {
        request: requestEventRegistrants,
        success: receiveEventRegistrants,
        failure: eventRegistrantsRequestError,
      },
    },
  };
};

export const setEventHost = (
  eventId: string,
  hostUserId: string
): callApiReturnType => ({
  type: CALL_API,
  payload: {
    method: "PATCH",
    endpoint: `/api/events/events/${eventId}/hosts/${hostUserId}`,
    actionParams: {
      eventId,
      hostUserId,
    },
    actions: {
      request: requestSetEventHost,
      success: receiveSetEventHost,
      failure: setEventHostRequestError,
    },
    toasts: {
      success: "Event instructor updated",
      failure: "Couldn't update event instructor",
    },
  },
});

export const updateCabelasClassEvent = (
  eventId: string,
  updates: EventUpdateValues
): callApiReturnType => ({
  type: CALL_API,
  payload: {
    method: "PATCH",
    endpoint: `${EVENTS_API_ENDPOINTS.EVENTS}/${eventId}`,
    options: updates,
    actionParams: {
      eventId,
      updates,
    },
    actions: {
      request: requestUpdateEvent,
      success: receiveUpdateEvent,
      failure: updateEventRequestError,
    },
    toasts: {
      success: "Changes saved and submitted for review",
      failure: "Couldn't update Cabela's class",
    },
  },
});

export const getEventReviewActions = (eventId: string): callApiReturnType => ({
  type: CALL_API,
  payload: {
    method: "GET",
    endpoint: EVENTS_API_ENDPOINTS.REVIEW_ACTIONS(eventId),
    actionParams: { eventId },
    actions: {
      request: requestEventReviewActions,
      success: receiveEventReviewActions,
      failure: eventReviewActionsRequestError,
    },
    disableErrorToast: true,
  },
});

export const getReviewStatusCounts = (
  businessId: string
): callApiReturnType => ({
  type: CALL_API,
  payload: {
    method: "GET",
    endpoint: `${
      EVENTS_API_ENDPOINTS.REVIEW_STATUS_COUNTS
    }?${queryString.stringify({
      "filter[businessId][_eq]": businessId,
      "filter[status][_neq]": CANCELLED_STATUS,
    })}`,
    actionParams: { ...requestReviewStatusCounts().payload, businessId },
    actions: {
      request: requestReviewStatusCounts,
      success: receiveReviewStatusCounts,
      failure: reviewStatusCountsRequestError,
    },
    disableErrorToast: true,
  },
});

export const createEventReviewAction = (
  eventId: string,
  actionNote: string
): callApiReturnType => ({
  type: CALL_API,
  payload: {
    method: "POST",
    endpoint: EVENTS_API_ENDPOINTS.REVIEW_ACTIONS(eventId),
    options: {
      actionType: PENDING_REVIEW_STATUS,
      actionNote,
    },
    actionParams: {
      eventId,
      actionNote,
    },
    actions: {
      request: requestCreateEventReviewAction,
      success: receiveCreateEventReviewAction,
      failure: createEventReviewActionRequestError,
    },
    disableErrorToast: true,
    toasts: {
      success: "Changes submitted for review",
    },
  },
});

export const checkInRegistrant = (registrantId: string): callApiReturnType => ({
  type: CALL_API,
  payload: {
    method: "POST",
    endpoint: `/api/events/registrants/${registrantId}/check-in`,
    actionParams: {
      registrantId,
    },
    actions: {
      request: requestCheckInRegistrant,
      success: receiveCheckInRegistrant,
      failure: checkInRegistrantRequestError,
    },
    toasts: {
      failure: "Couldn't check in registrant",
    },
  },
});

export type State = {
  status: string,
  errorMessage: string,
  errors: apiErrorReturn,
  events: {
    [string]: EventRecordI,
  },
  eventIds: Array<string>,
  count: number,
  total: number,
  perPage: number,
  currentPage: number,
  totalPages: number,
  eventStatus: string,
  reviewStatus: string,
  eventView: string,
  search: string,
  sortColumn: string,
  sortDirection: string,
  classesRequestId: number,
  rosterEventStatus: string,
  rosterEventErrorMessage: string,
  rosterEventErrors: apiErrorReturn,
  rosterEventRequestId: number,
  rosterStatus: string,
  rosterErrorMessage: string,
  rosterErrors: apiErrorReturn,
  registrants: {
    [string]: EventRegistrantRecordI,
  },
  registrantIds: Array<string>,
  registrantsCount: number,
  registrantsTotal: number,
  registrantsPerPage: number,
  registrantsCurrentPage: number,
  registrantsTotalPages: number,
  registrantSearch: string,
  registrantSortColumn: string,
  registrantSortDirection: string,
  rosterRequestId: number,
  eventHostStatus: string,
  checkInStatus: string,
  eventUpdateStatus: string,
  pendingEventUpdateIds: Array<string>,
  reviewActionsByEvent: {
    [string]: Array<ReviewActionRecordI>,
  },
  reviewActionStatusByEvent: {
    [string]: string,
  },
  reviewActionErrorMessageByEvent: {
    [string]: string,
  },
  reviewActionErrorsByEvent: {
    [string]: apiErrorReturn,
  },
  reviewStatusCounts: ReviewStatusCounts,
  reviewStatusCountsStatus: string,
  reviewStatusCountsErrorMessage: string,
  reviewStatusCountsErrors: apiErrorReturn,
  reviewStatusCountsRequestId: number,
  reviewActionCreateStatus: string,
  reviewActionCreateEventId: string,
  reviewActionCreateErrorMessage: string,
  reviewActionCreateErrors: apiErrorReturn,
  businessId: string,
  businessStatus: string,
};

const initialState: State = {
  status: NONE,
  errorMessage: "",
  errors: [],
  events: {},
  eventIds: [],
  count: 0,
  total: 0,
  perPage: DEFAULT_PER_PAGE,
  currentPage: 1,
  totalPages: 0,
  eventStatus: LIVE_STATUS,
  reviewStatus: ALL_REVIEW_STATUSES,
  eventView: UPCOMING_VIEW,
  search: "",
  sortColumn: DEFAULT_SORT_COLUMN,
  sortDirection: DEFAULT_SORT_DIRECTION,
  classesRequestId: 0,
  rosterEventStatus: NONE,
  rosterEventErrorMessage: "",
  rosterEventErrors: [],
  rosterEventRequestId: 0,
  rosterStatus: NONE,
  rosterErrorMessage: "",
  rosterErrors: [],
  registrants: {},
  registrantIds: [],
  registrantsCount: 0,
  registrantsTotal: 0,
  registrantsPerPage: DEFAULT_PER_PAGE,
  registrantsCurrentPage: 1,
  registrantsTotalPages: 0,
  registrantSearch: "",
  registrantSortColumn: "createdAt",
  registrantSortDirection: DEFAULT_SORT_DIRECTION,
  rosterRequestId: 0,
  eventHostStatus: NONE,
  checkInStatus: NONE,
  eventUpdateStatus: NONE,
  pendingEventUpdateIds: [],
  reviewActionsByEvent: {},
  reviewActionStatusByEvent: {},
  reviewActionErrorMessageByEvent: {},
  reviewActionErrorsByEvent: {},
  reviewStatusCounts: {
    total: 0,
    pending: 0,
    approved: 0,
    flagged: 0,
    denied: 0,
  },
  reviewStatusCountsStatus: NONE,
  reviewStatusCountsErrorMessage: "",
  reviewStatusCountsErrors: [],
  reviewStatusCountsRequestId: 0,
  reviewActionCreateStatus: NONE,
  reviewActionCreateEventId: "",
  reviewActionCreateErrorMessage: "",
  reviewActionCreateErrors: [],
  businessId: "",
  businessStatus: NONE,
};

const instructorCabelasClasses = handleActions(
  {
    [CABELAS_CLASSES_REQUEST]: (
      state: State,
      action: RequestCabelasClassesReturn
    ): State => {
      const {
        eventStatus,
        reviewStatus,
        eventView,
        currentPage,
        perPage,
        search,
        sortColumn,
        sortDirection,
        requestId,
      } = action.payload;

      return {
        ...state,
        eventStatus,
        reviewStatus,
        eventView,
        currentPage,
        perPage,
        search,
        sortColumn,
        sortDirection,
        status: LOADING,
        classesRequestId: requestId,
        errorMessage: "",
        errors: [],
      };
    },
    [CABELAS_CLASSES_REQUEST_SUCCESS]: (
      state: State,
      action: ReceiveCabelasClassesReturn
    ): State => {
      if (action.meta.requestId !== state.classesRequestId) {
        return state;
      }

      const { data = [], meta = {} } = action.payload;
      const { events, eventIds } = parseEvents(data);

      return {
        ...state,
        status: LOADED,
        events,
        eventIds,
        count: metaValue(meta, "count", data.length),
        total: metaValue(meta, "total", data.length),
        perPage: metaValue(meta, "perPage", state.perPage),
        currentPage: metaValue(meta, "currentPage", state.currentPage),
        totalPages: metaValue(meta, "totalPages", 1),
      };
    },
    [CABELAS_CLASSES_REQUEST_ERROR]: (
      state: State,
      action: CabelasApiErrorAction
    ): State => {
      if (errorRequestId(action) !== state.classesRequestId) {
        return state;
      }

      const {
        payload: { errorMessage, errors },
      } = action;
      return {
        ...state,
        status: ERROR,
        errorMessage,
        errors,
      };
    },
    [ROSTER_EVENT_REQUEST]: (
      state: State,
      action: RequestRosterEventReturn
    ): State => ({
      ...state,
      rosterEventStatus: LOADING,
      rosterEventErrorMessage: "",
      rosterEventErrors: [],
      rosterEventRequestId: action.payload.requestId,
    }),
    [ROSTER_EVENT_REQUEST_SUCCESS]: (
      state: State,
      action: ReceiveRosterEventReturn
    ): State => {
      if (action.meta.requestId !== state.rosterEventRequestId) {
        return state;
      }

      const event = parseEventReturnToRecord(action.payload.data);

      return {
        ...state,
        rosterEventStatus: LOADED,
        events: {
          ...state.events,
          [event.id]: event,
        },
      };
    },
    [ROSTER_EVENT_REQUEST_ERROR]: (
      state: State,
      action: CabelasApiErrorAction
    ): State => {
      if (errorRequestId(action) !== state.rosterEventRequestId) {
        return state;
      }

      const {
        payload: { errorMessage, errors },
      } = action;

      return {
        ...state,
        rosterEventStatus: ERROR,
        rosterEventErrorMessage: errorMessage,
        rosterEventErrors: errors,
      };
    },
    [EVENT_REGISTRANTS_REQUEST]: (
      state: State,
      action: RequestEventRegistrantsReturn
    ): State => ({
      ...state,
      rosterStatus: LOADING,
      rosterErrorMessage: "",
      rosterErrors: [],
      rosterRequestId: action.payload.requestId,
      registrantsPerPage: action.payload.perPage,
      registrantsCurrentPage: action.payload.currentPage,
      registrantSearch: action.payload.search,
      registrantSortColumn: action.payload.sortColumn,
      registrantSortDirection: action.payload.sortDirection,
    }),
    [EVENT_REGISTRANTS_REQUEST_SUCCESS]: (
      state: State,
      action: ReceiveEventRegistrantsReturn
    ): State => {
      if (action.meta.requestId !== state.rosterRequestId) {
        return state;
      }

      const { data = [], meta = {} } = action.payload;
      const { registrants, registrantIds } = parseRegistrants(data);

      registrantIds.forEach((id: string) => {
        const currentRegistrant = state.registrants[id];

        if (currentRegistrant && currentRegistrant.isCheckInPending) {
          registrants[id] = currentRegistrant;
        }
      });

      return {
        ...state,
        rosterStatus: LOADED,
        registrants,
        registrantIds,
        registrantsCount: metaValue(meta, "count", data.length),
        registrantsTotal: metaValue(meta, "total", data.length),
        registrantsPerPage: metaValue(
          meta,
          "perPage",
          state.registrantsPerPage
        ),
        registrantsCurrentPage: metaValue(
          meta,
          "currentPage",
          state.registrantsCurrentPage
        ),
        registrantsTotalPages: metaValue(meta, "totalPages", 1),
      };
    },
    [EVENT_REGISTRANTS_REQUEST_ERROR]: (
      state: State,
      action: CabelasApiErrorAction
    ): State => {
      if (errorRequestId(action) !== state.rosterRequestId) {
        return state;
      }

      const {
        payload: { errorMessage, errors },
      } = action;

      return {
        ...state,
        rosterStatus: ERROR,
        rosterErrorMessage: errorMessage,
        rosterErrors: errors,
      };
    },
    [EVENT_HOST_REQUEST]: (state: State): State => ({
      ...state,
      eventHostStatus: PENDING,
    }),
    [EVENT_HOST_REQUEST_SUCCESS]: (
      state: State,
      action: EventHostAction
    ): State => {
      const { eventId, hostUserId } = action.payload;
      const event = state.events[eventId];

      if (!event) {
        return {
          ...state,
          eventHostStatus: SUCCESS,
        };
      }

      return {
        ...state,
        eventHostStatus: SUCCESS,
        events: {
          ...state.events,
          [eventId]: event.set("hostAttendeeId", hostUserId),
        },
      };
    },
    [EVENT_HOST_REQUEST_ERROR]: (state: State): State => ({
      ...state,
      eventHostStatus: ERROR,
    }),
    [EVENT_UPDATE_REQUEST]: (
      state: State,
      action: EventUpdateAction
    ): State => ({
      ...state,
      eventUpdateStatus: PENDING,
      pendingEventUpdateIds: state.pendingEventUpdateIds.includes(
        action.payload.eventId
      )
        ? state.pendingEventUpdateIds
        : [...state.pendingEventUpdateIds, action.payload.eventId],
    }),
    [EVENT_UPDATE_REQUEST_SUCCESS]: (
      state: State,
      action: EventUpdateAction
    ): State => {
      const { eventId, updates, event: responseEvent } = action.payload;
      const pendingEventUpdateIds = state.pendingEventUpdateIds.filter(
        (id: string): boolean => id !== eventId
      );
      const reviewActionsByEvent = { ...state.reviewActionsByEvent };
      const reviewActionStatusByEvent = {
        ...state.reviewActionStatusByEvent,
      };

      delete reviewActionsByEvent[eventId];
      delete reviewActionStatusByEvent[eventId];

      if (updates.status === CANCELLED_STATUS) {
        const events = { ...state.events };
        delete events[eventId];

        return {
          ...state,
          eventUpdateStatus: SUCCESS,
          pendingEventUpdateIds,
          reviewActionsByEvent,
          reviewActionStatusByEvent,
          events,
          eventIds: state.eventIds.filter(
            (id: string): boolean => id !== eventId
          ),
          count: Math.max(state.count - 1, 0),
          total: Math.max(state.total - 1, 0),
        };
      }

      const event = state.events[eventId];
      const reconciledEvent = responseEvent
        ? parseEventReturnToRecord(responseEvent)
        : null;

      if (!event && !reconciledEvent) {
        return {
          ...state,
          eventUpdateStatus: SUCCESS,
          pendingEventUpdateIds,
          reviewActionsByEvent,
          reviewActionStatusByEvent,
        };
      }

      return {
        ...state,
        eventUpdateStatus: SUCCESS,
        pendingEventUpdateIds,
        reviewActionsByEvent,
        reviewActionStatusByEvent,
        events: {
          ...state.events,
          [eventId]:
            reconciledEvent ||
            event
              .set("status", updates.status || event.status)
              .set("reviewStatus", PENDING_REVIEW_STATUS)
              .set("startDate", updates.startDate || event.startDate)
              .set("endDate", updates.endDate || event.endDate),
        },
      };
    },
    [EVENT_UPDATE_REQUEST_ERROR]: (
      state: State,
      action: CabelasApiErrorAction
    ): State => {
      const { actionParams } = action.payload;
      const eventId = actionParams ? actionParams.eventId : "";

      return {
        ...state,
        eventUpdateStatus: ERROR,
        pendingEventUpdateIds: state.pendingEventUpdateIds.filter(
          (id: string): boolean => id !== eventId
        ),
      };
    },
    [EVENT_REVIEW_ACTIONS_REQUEST]: (
      state: State,
      action: EventReviewActionsAction
    ): State => ({
      ...state,
      reviewActionStatusByEvent: {
        ...state.reviewActionStatusByEvent,
        [action.payload.eventId]: LOADING,
      },
      reviewActionErrorMessageByEvent: {
        ...state.reviewActionErrorMessageByEvent,
        [action.payload.eventId]: "",
      },
      reviewActionErrorsByEvent: {
        ...state.reviewActionErrorsByEvent,
        [action.payload.eventId]: [],
      },
    }),
    [EVENT_REVIEW_ACTIONS_REQUEST_SUCCESS]: (
      state: State,
      action: EventReviewActionsAction
    ): State => ({
      ...state,
      reviewActionsByEvent: {
        ...state.reviewActionsByEvent,
        [action.payload.eventId]: action.payload.reviewActions || [],
      },
      reviewActionStatusByEvent: {
        ...state.reviewActionStatusByEvent,
        [action.payload.eventId]: LOADED,
      },
    }),
    [EVENT_REVIEW_ACTIONS_REQUEST_ERROR]: (
      state: State,
      action: CabelasApiErrorAction
    ): State => {
      const { actionParams, errorMessage, errors } = action.payload;
      const eventId = (actionParams && actionParams.eventId) || "";

      return {
        ...state,
        reviewActionStatusByEvent: {
          ...state.reviewActionStatusByEvent,
          [eventId]: ERROR,
        },
        reviewActionErrorMessageByEvent: {
          ...state.reviewActionErrorMessageByEvent,
          [eventId]: errorMessage,
        },
        reviewActionErrorsByEvent: {
          ...state.reviewActionErrorsByEvent,
          [eventId]: errors,
        },
      };
    },
    [REVIEW_STATUS_COUNTS_REQUEST]: (
      state: State,
      action: RequestReviewStatusCountsReturn
    ): State => ({
      ...state,
      reviewStatusCountsStatus: LOADING,
      reviewStatusCountsErrorMessage: "",
      reviewStatusCountsErrors: [],
      reviewStatusCountsRequestId: action.payload.requestId,
    }),
    [REVIEW_STATUS_COUNTS_REQUEST_SUCCESS]: (
      state: State,
      action: ReceiveReviewStatusCountsReturn
    ): State => {
      if (action.meta.requestId !== state.reviewStatusCountsRequestId) {
        return state;
      }

      return {
        ...state,
        reviewStatusCountsStatus: LOADED,
        reviewStatusCounts: action.payload || state.reviewStatusCounts,
      };
    },
    [REVIEW_STATUS_COUNTS_REQUEST_ERROR]: (
      state: State,
      action: CabelasApiErrorAction
    ): State => {
      if (errorRequestId(action) !== state.reviewStatusCountsRequestId) {
        return state;
      }

      return {
        ...state,
        reviewStatusCountsStatus: ERROR,
        reviewStatusCountsErrorMessage: action.payload.errorMessage,
        reviewStatusCountsErrors: action.payload.errors,
      };
    },
    [EVENT_REVIEW_ACTION_CREATE_REQUEST]: (
      state: State,
      action: CreateEventReviewActionAction
    ): State => ({
      ...state,
      reviewActionCreateStatus: PENDING,
      reviewActionCreateEventId: action.payload.eventId,
      reviewActionCreateErrorMessage: "",
      reviewActionCreateErrors: [],
    }),
    [EVENT_REVIEW_ACTION_CREATE_SUCCESS]: (
      state: State,
      action: CreateEventReviewActionAction
    ): State => {
      const { eventId, reviewAction } = action.payload;
      const event = state.events[eventId];
      const existingActions = state.reviewActionsByEvent[eventId] || [];

      return {
        ...state,
        reviewActionCreateStatus: SUCCESS,
        reviewActionCreateEventId: eventId,
        events: event
          ? {
              ...state.events,
              [eventId]: event.set("reviewStatus", PENDING_REVIEW_STATUS),
            }
          : state.events,
        reviewActionsByEvent: reviewAction
          ? {
              ...state.reviewActionsByEvent,
              [eventId]: [...existingActions, reviewAction],
            }
          : state.reviewActionsByEvent,
      };
    },
    [EVENT_REVIEW_ACTION_CREATE_ERROR]: (
      state: State,
      action: CabelasApiErrorAction
    ): State => ({
      ...state,
      reviewActionCreateStatus: ERROR,
      reviewActionCreateErrorMessage: action.payload.errorMessage,
      reviewActionCreateErrors: action.payload.errors,
    }),
    [CHECK_IN_REQUEST]: (
      state: State,
      action: CheckInRegistrantAction
    ): State => {
      const { registrantId } = action.payload;
      const registrant = state.registrants[registrantId];

      if (!registrant) {
        return {
          ...state,
          checkInStatus: PENDING,
        };
      }

      return {
        ...state,
        checkInStatus: PENDING,
        registrants: {
          ...state.registrants,
          [registrantId]: registrant.set("isCheckInPending", true),
        },
      };
    },
    [CHECK_IN_REQUEST_SUCCESS]: (
      state: State,
      action: CheckInRegistrantAction
    ): State => {
      const { registrantId } = action.payload;
      const registrant = state.registrants[registrantId];

      if (!registrant) {
        return {
          ...state,
          checkInStatus: SUCCESS,
        };
      }

      return {
        ...state,
        checkInStatus: SUCCESS,
        registrants: {
          ...state.registrants,
          [registrantId]: registrant
            .set("checkedIn", true)
            .set("isCheckInPending", false),
        },
      };
    },
    [CHECK_IN_REQUEST_ERROR]: (
      state: State,
      action: CabelasApiErrorAction
    ): State => {
      const { actionParams } = action.payload;
      const registrantId = (actionParams && actionParams.registrantId) || "";
      const registrant = state.registrants[registrantId];

      if (!registrant) {
        return {
          ...state,
          checkInStatus: ERROR,
        };
      }

      const registrants = { ...state.registrants };
      registrants[registrantId] = registrant.set("isCheckInPending", false);

      return {
        ...state,
        checkInStatus: ERROR,
        registrants,
      };
    },
    [CLEAR_ROSTER]: (state: State): State => ({
      ...state,
      rosterEventStatus: NONE,
      rosterEventErrorMessage: "",
      rosterEventErrors: [],
      rosterEventRequestId: 0,
      rosterStatus: NONE,
      rosterErrorMessage: "",
      rosterErrors: [],
      registrants: {},
      registrantIds: [],
      registrantsCount: 0,
      registrantsTotal: 0,
      registrantsTotalPages: 0,
      registrantSearch: "",
      registrantSortColumn: "createdAt",
      registrantSortDirection: DEFAULT_SORT_DIRECTION,
      rosterRequestId: 0,
      eventHostStatus: NONE,
      checkInStatus: NONE,
      eventUpdateStatus: NONE,
      pendingEventUpdateIds: [],
    }),
    [BUSINESS_REQUEST]: (state: State): State => ({
      ...state,
      businessStatus: LOADING,
    }),
    [BUSINESS_REQUEST_SUCCESS]: (
      state: State,
      action: ReceiveBusinessReturn
    ): State => ({
      ...state,
      businessStatus: LOADED,
      businessId: action.payload?.data?.id || "",
    }),
    [BUSINESS_REQUEST_ERROR]: (state: State): State => ({
      ...state,
      businessStatus: ERROR,
    }),
    [CLEAR]: (): State => initialState,
  },
  initialState
);

export default instructorCabelasClasses;
